// @ts-check
import { z } from "zod";

// ---------------------------------------------------------------------------
// WebSocket close codes used by the control plane
// ---------------------------------------------------------------------------
//
// Clients should treat these as authoritative when classifying connection
// closures (fatal vs transient). Codes are surfaced via the WebSocket
// `CloseEvent.code` and a human-readable `CloseEvent.reason`.
//
//   1003  invalid message       Frame failed JSON parse or schema validation.
//                               Fatal — client bug, don't auto-reconnect.
//   1008  policy violation      Specifically: first frame was not a Hello, or
//                               role-based routing was violated. Fatal.
//   1011  internal error        Server-side exception. Transient — reconnect.
//   4001  displaced             Another connection arrived claiming the same
//                               participant id; the older socket is closed
//                               in favor of the new one. Fatal — old client
//                               should NOT auto-reconnect into a ping-pong
//                               with the displacer.
//
// Codes in the 1000-1004 range and 4000-4999 are reserved for application
// use per RFC 6455; the 4xxx range is the canonical place to define
// protocol-specific reasons.
//
// ---------------------------------------------------------------------------

/** Fatal close codes — receiving any of these means the client should stop trying to reconnect. */
export const FATAL_CLOSE_CODES = new Set<number>([1003, 1008, 4001]);

/** Displaced: another connection with the same participant id has joined. */
export const CLOSE_DISPLACED = 4001;

// ---------------------------------------------------------------------------
// Identifiers
// ---------------------------------------------------------------------------

/** Server-authenticated participant role. Renderers run LensCore; controls drive them. */
export const RoleSchema = z.enum(["renderer", "control"]);
export type Role = z.infer<typeof RoleSchema>;

/** Stable participant id within a room. Free-form string; the server treats it opaquely. */
export const ParticipantIdSchema = z.string().min(1).max(256);
export type ParticipantId = z.infer<typeof ParticipantIdSchema>;

/** Opaque correlation token for command/response pairs. */
export const RequestIdSchema = z.string().min(1).max(256);
export type RequestId = z.infer<typeof RequestIdSchema>;

// ---------------------------------------------------------------------------
// Control-plane messages (room membership, no engine semantics)
// ---------------------------------------------------------------------------

/** First message sent after connect. `role` must match the server-assigned connection role. */
export const HelloSchema = z.object({
  type: z.literal("hello"),
  role: RoleSchema,
  id: ParticipantIdSchema,
});
export type Hello = z.infer<typeof HelloSchema>;

/** Broadcast by the server when room membership changes. */
export const PresenceSchema = z.object({
  type: z.literal("presence"),
  clients: z.array(
    z.object({
      id: ParticipantIdSchema,
      role: RoleSchema,
    }),
  ),
});
export type Presence = z.infer<typeof PresenceSchema>;

/** Pushed by the server to a newly-connected control: latest known state of each renderer. */
export const SnapshotSchema = z.object({
  type: z.literal("snapshot"),
  renderers: z.record(ParticipantIdSchema, z.unknown()),
});
export type Snapshot = z.infer<typeof SnapshotSchema>;

// ---------------------------------------------------------------------------
// Engine-control messages (mirror the LensHost contract)
// ---------------------------------------------------------------------------

/**
 * A command targeted at one or all renderers in a room.
 *
 * `target` is optional: omitting it broadcasts to every renderer; including
 * a participant id routes to that one specifically.
 */
export const CommandSchema = z.object({
  type: z.literal("command"),
  requestId: RequestIdSchema,
  command: z.string().min(1),
  payload: z.unknown().optional(),
  target: ParticipantIdSchema.optional(),
});
export type Command = z.infer<typeof CommandSchema>;

/** Stable stages used by browser, React, Electron, and remote-control clients. */
export const LensLoadStageSchema = z.enum(["fetch", "modify", "engine", "runtime"]);
export type LensLoadStage = z.infer<typeof LensLoadStageSchema>;

/** Known failure classes. `UNKNOWN` keeps newer producers compatible with older clients. */
export const LensLoadErrorCodeSchema = z.enum([
  "FETCH_FAILED",
  "INVALID_LENS_RESPONSE",
  "INVALID_LENS_SOURCE",
  "MODIFICATION_FAILED",
  "ENGINE_NOT_READY",
  "ENGINE_LOAD_FAILED",
  "RUNTIME_EXCEPTION",
  "REMOTE_ASSET_FAILED",
  "FIRST_FRAME_TIMEOUT",
  "UNKNOWN",
]);
export type LensLoadErrorCode = z.infer<typeof LensLoadErrorCodeSchema>;

/** JSON-safe error representation preserved across postMessage/WebSocket/Electron IPC. */
export const SerializedErrorSchema = z.object({
  name: z.string().optional(),
  message: z.string(),
  stack: z.string().nullable().optional(),
  code: LensLoadErrorCodeSchema.optional(),
  stage: LensLoadStageSchema.optional(),
  lensId: z.string().nullable().optional(),
  recoverable: z.boolean().optional(),
  originalPreserved: z.boolean().optional(),
  details: z.unknown().optional(),
});
export type SerializedError = z.infer<typeof SerializedErrorSchema>;

/**
 * The strict wire shape emitted by `LensLoadError.toJSON()`. Unlike a generic
 * command error, every field a UI needs for branching is required.
 */
export const SerializedLensLoadErrorSchema = SerializedErrorSchema.extend({
  name: z.literal("LensLoadError"),
  code: LensLoadErrorCodeSchema,
  stage: LensLoadStageSchema,
  lensId: z.string().nullable(),
  recoverable: z.boolean(),
  originalPreserved: z.boolean(),
});
export type SerializedLensLoadError = z.infer<typeof SerializedLensLoadErrorSchema>;

/** Acknowledgement of a Command. Echoes requestId so the caller can correlate. */
export const CommandResultSchema = z.object({
  type: z.literal("commandResult"),
  requestId: RequestIdSchema,
  ok: z.boolean(),
  result: z.unknown().optional(),
  error: SerializedErrorSchema.optional(),
});
export type CommandResult = z.infer<typeof CommandResultSchema>;

/** Per-frame LensCore processing-time aggregates published at a bounded rate. */
export const ProcessingTimeMetricsSchema = z.object({
  latestMs: z.number().nonnegative().nullable(),
  meanMs: z.number().nonnegative(),
  stdDevMs: z.number().nonnegative(),
  medianMs: z.number().nonnegative(),
  p95Ms: z.number().nonnegative(),
  p99Ms: z.number().nonnegative(),
  maxMs: z.number().nonnegative(),
  sampleCount: z.number().int().nonnegative(),
  // Optional because cached v1 renderers predate missing-sample accounting.
  missingSampleCount: z.number().int().nonnegative().optional(),
  slowFrameThresholdMs: z.number().nonnegative(),
  slowFrameCount: z.number().int().nonnegative(),
  overflowThresholdMs: z.number().int().positive(),
  overflowCount: z.number().int().nonnegative(),
});
export type ProcessingTimeMetrics = z.infer<typeof ProcessingTimeMetricsSchema>;

/** Stable metrics payload mirrored from the renderer's core metrics store. */
export const EngineMetricsSchema = z.object({
  protocol: z.literal("lens-engine-metrics/v1"),
  frameCount: z.number().nonnegative(),
  /** LensCore completed-frame callback cadence, not native/display output FPS. */
  frameCallbackFps: z.number().nonnegative().optional(),
  smoothedFrameCallbackFps: z.number().nonnegative().optional(),
  /** Deprecated aliases retained so rolling deployments accept cached renderers. */
  outputFps: z.number().nonnegative().optional(),
  smoothedFps: z.number().nonnegative().optional(),
  lastFrameCompletedAtMs: z.number().nonnegative().nullable(),
  processingTimeMs: ProcessingTimeMetricsSchema,
});
export type EngineMetrics = z.infer<typeof EngineMetricsSchema>;

export const LensHealthSchema = z.enum(["idle", "loading", "healthy", "degraded", "failed"]);
export type LensHealth = z.infer<typeof LensHealthSchema>;

export const LensDiagnosticCodeSchema = z.enum([
  "FETCH_FAILED", "INVALID_LENS_RESPONSE", "INVALID_LENS_SOURCE", "MODIFICATION_FAILED",
  "ENGINE_NOT_READY", "ENGINE_LOAD_FAILED", "FIRST_FRAME_TIMEOUT", "REMOTE_ASSET_FAILED",
  "RUNTIME_EXCEPTION", "UNKNOWN", "ENGINE_INITIALIZATION_FAILED", "LENS_DESERIALIZATION",
  "LENS_VALIDATION_FAILED", "LENS_EXECUTION_FAILED", "ENGINE_UNINITIALIZED",
  "VIDEO_PLAYBACK_FAILED", "VIDEO_PLAYBACK_MUTED", "SCRIPT_ASYNC_EXCEPTION",
  "SHADER_COMPILE_REJECTED", "PROGRAM_LINK_REJECTED", "ERROR_PATTERN_COMPILED",
  "ERROR_PATTERN_DRAWN", "GL_ERROR", "FRAMEBUFFER_INCOMPLETE",
  "RUNTIME_INSTANTIATION_FAILED", "ENGINE_ABORTED", "NATIVE_STDERR",
]);
export type LensDiagnosticCode = z.infer<typeof LensDiagnosticCodeSchema>;

export const LensDiagnosticIssueSchema = z.object({
  id: z.string(),
  code: LensDiagnosticCodeSchema,
  severity: z.enum(["debug", "warning", "error", "fatal"]),
  source: z.enum(["host", "lens-script", "lenscore-native", "webgl"]),
  message: z.string(),
  lensId: z.string().nullable(),
  recoverable: z.boolean(),
  recommendedAction: z.enum(["none", "remove-lens", "destroy-session", "prompt-for-playback"]),
  active: z.boolean(),
  count: z.number().int().positive(),
  firstSeenAt: z.number(),
  lastSeenAt: z.number(),
  details: z.unknown().optional(),
});
export type LensDiagnosticIssue = z.infer<typeof LensDiagnosticIssueSchema>;

export const LensErrorSnapshotSchema = z.object({
  protocol: z.literal("lens-error-monitor/v1"),
  health: LensHealthSchema,
  lensId: z.string().nullable(),
  loadGeneration: z.number().int().nonnegative(),
  loadState: z.enum(["idle", "loading", "loaded", "failed"]),
  resourcesLoaded: z.boolean(),
  firstFrameSeen: z.boolean(),
  frameCount: z.number().nonnegative(),
  issues: z.array(LensDiagnosticIssueSchema),
  activeIssues: z.array(LensDiagnosticIssueSchema),
  lastIssue: LensDiagnosticIssueSchema.nullable(),
});
export type LensErrorSnapshot = z.infer<typeof LensErrorSnapshotSchema>;

/**
 * Periodic engine state from a renderer. Open shape — additional fields
 * are permitted and forwarded verbatim.
 */
export const StateSchema = z
  .object({
    type: z.literal("state"),
    ready: z.boolean().optional(),
    source: z.unknown().nullable().optional(),
    frameCount: z.number().optional(),
    measuredFps: z.number().optional(),
    engineMetrics: EngineMetricsSchema.optional(),
    lensErrors: LensErrorSnapshotSchema.optional(),
    activeLenses: z.array(z.unknown()).optional(),
    coreVersion: z.number().nullable().optional(),
    deviceClass: z.union([z.string(), z.number()]).nullable().optional(),
    error: z.string().nullable().optional(),
    lensError: SerializedLensLoadErrorSchema.nullable().optional(),
  })
  .passthrough();
export type State = z.infer<typeof StateSchema>;

/** One-shot boot handshake: renderer signals its engine is ready. */
export const ReadySchema = z.object({
  type: z.literal("ready"),
  payload: z.unknown().optional(),
});
export type Ready = z.infer<typeof ReadySchema>;

/** Structured log entry from a renderer. */
export const LogSchema = z.object({
  type: z.literal("log"),
  level: z.enum(["info", "warn", "error"]),
  message: z.string(),
  detail: z.unknown().optional(),
});
export type Log = z.infer<typeof LogSchema>;

// ---------------------------------------------------------------------------
// Discriminated unions used at protocol boundaries
// ---------------------------------------------------------------------------

/**
 * Any message a client is allowed to SEND. Controls send Hello + Command;
 * renderers send Hello + CommandResult + State + Ready + Log. Validation
 * by role happens server-side.
 */
export const ClientMessageSchema = z.discriminatedUnion("type", [
  HelloSchema,
  CommandSchema,
  CommandResultSchema,
  StateSchema,
  ReadySchema,
  LogSchema,
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

/**
 * Any message the server can RELAY OR PRODUCE. Includes everything from
 * ClientMessage that the server forwards on, plus its own Presence and
 * Snapshot synthesis.
 */
export const ServerMessageSchema = z.discriminatedUnion("type", [
  CommandSchema,
  CommandResultSchema,
  StateSchema,
  ReadySchema,
  LogSchema,
  PresenceSchema,
  SnapshotSchema,
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;

// ---------------------------------------------------------------------------
// Convenience helpers
// ---------------------------------------------------------------------------

/**
 * Safely parse a string (e.g. a WebSocket text frame, a postMessage payload
 * already JSON.stringify'd) into a ClientMessage, returning null on parse
 * failure rather than throwing. Useful in receive loops where invalid
 * messages should be dropped, not crash the handler.
 */
export function parseClientMessage(raw: unknown): ClientMessage | null {
  let candidate: unknown = raw;
  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return null;
    }
  }
  const result = ClientMessageSchema.safeParse(candidate);
  return result.success ? result.data : null;
}

/** Mirror of {@link parseClientMessage} for messages flowing in the server→client direction. */
export function parseServerMessage(raw: unknown): ServerMessage | null {
  let candidate: unknown = raw;
  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return null;
    }
  }
  const result = ServerMessageSchema.safeParse(candidate);
  return result.success ? result.data : null;
}
